import React from 'react';
import withMainLayout from 'layouts/main';
import dynamic from 'next/dynamic';
import {Grid, Row, Col} from '@/grid';
import Container from '@/container';
import {SectionPadding, SectionPaddingHalfTop, SectionPaddingHalfBottom} from '@/section';
import {H1, H2, P, PMarginSm} from '@/typography';
import {BreakVisibleXxl} from '@/breaks';
import {withGreen800, withBgNeutral200Rounded} from '@/hocs/withColors';
import {withMarginBottomZeroToMd} from '@/hocs/withSpacing';
import PictureWrapper from '@/ui/images-wrapper';
import {ListOlNumbered, ListDisc} from '@/ui/lists/list-icons';
import {GridLayout, GridItem} from '@/ui/grid/grid-layout';
import BoxGrid from '@/ui/box-grid';

const H1Green800 = withGreen800(H1);
const DivBgNeutral200Rounded = withBgNeutral200Rounded('div');
const DivMarginedMobile = withMarginBottomZeroToMd('div');
const imgPath = '/static/images/developers/sourcing/';

const SubmitPartnerApplication = dynamic(
  import('@/content/developers/submit-partner-application'),
  {
    loading: () => null
  }
);

const seoMetas = {
  title: 'Sourcing tool integration | Workable developer partners',
  description:
    'Integrate your candidate sourcing tool with Workable. Help your customers import candidate profiles directly into Workable\'s applicant tracking system.',
  canonical: 'https://www.workable.com/developers/partner-program/sourcing'
};

const SourcingPartnerPage = props => (
  <>
    <SectionPaddingHalfBottom>
      <Container variant="section-padding-below-nav">
        <SectionPaddingHalfTop>
          <Grid fluid>
            <Row middle="lg">
              <Col xs={12} lg={6}>
                <H1Green800 className="heading-1-smaller">
                  Connect your sourcing tool
                  <BreakVisibleXxl /> with Workable
                </H1Green800>
                <PMarginSm>
                  Great hires start with great candidates. Connect your sourcing tool with Workable
                  so your customers can move candidates smoothly into their hiring process.
                </PMarginSm>
                <PMarginSm>
                  Customers start their hiring journey in Workable by looking at potential
                  candidates.
                </PMarginSm>
                <PMarginSm>These might come from:</PMarginSm>
                <DivMarginedMobile>
                  <DivBgNeutral200Rounded>
                    <ListOlNumbered>
                      <li>A resume uploaded from their computer</li>
                      <li>A profile imported directly from your sourcing tool</li>
                    </ListOlNumbered>
                  </DivBgNeutral200Rounded>
                </DivMarginedMobile>
              </Col>
              <Col xs={12} lg={6}>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="sourcing-hero"
                  imageTypeFallback="jpg"
                  alt="Connect your sourcing tool with Workable - image"
                  fetchpriority="high"
                  loading="eager"
                  width={584}
                  height={624}
                />
              </Col>
            </Row>
          </Grid>
        </SectionPaddingHalfTop>
      </Container>
    </SectionPaddingHalfBottom>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={7}>
              <GridItem>
                <PictureWrapper
                  imgPath={imgPath}
                  imageUrl="adding-profiles"
                  imageTypeFallback="png"
                  altImage="Adding profiles to Workable - image"
                />
              </GridItem>
            </Col>
            <Col xs={12} lg={5} first="xs" last="lg">
              <H2 className="heading-3">Adding profiles to Workable</H2>
              <PMarginSm>
                By connecting your tool with Workable, you let customers add candidates directly to
                their hiring pipeline.
              </PMarginSm>
              <PMarginSm>
                For example, when someone finds a candidate using Candidates page, they can add them
                straight to their job with one click.
              </PMarginSm>
              <PMarginSm>
                From there, the hiring team reviews the candidate and moves them through the process
                if they're a good fit.
              </PMarginSm>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>

    <SectionPadding>
      <Container>
        <Grid fluid>
          <Row middle="lg">
            <Col xs={12} lg={5}>
              <H2 className="heading-3">
                How customers connect <BreakVisibleXxl />
                Workable with sourcing tools
              </H2>
              <PMarginSm>It's easy for Workable users to activate your integration:</PMarginSm>
              <DivMarginedMobile>
                <DivBgNeutral200Rounded>
                  <ListDisc>
                    <li>They find it in the integrations list inside Workable</li>
                    <li>Existing users of both services follow simple on-screen instructions</li>
                    <li>New users see your logo, a description, and a link to learn more</li>
                  </ListDisc>
                </DivBgNeutral200Rounded>
              </DivMarginedMobile>
            </Col>
            <Col xs={12} lg={6} lgOffset={1}>
              <GridLayout Col={1} autoRows>
                <GridItem>
                  <BoxGrid
                    backgroundColor="var(--brown50)"
                    Url="https://workable.readme.io/"
                    UrlCopy="View API documentation"
                    target="_blank"
                    minHeight={288}>
                    <PMarginSm className="heading-3">API docs</PMarginSm>
                    <P>
                      See how you could start to integrate your product or service with Workable
                    </P>
                  </BoxGrid>
                </GridItem>
              </GridLayout>
            </Col>
          </Row>
        </Grid>
      </Container>
    </SectionPadding>
    <SubmitPartnerApplication />
  </>
);

export default withMainLayout(SourcingPartnerPage, {...seoMetas});

export {getSharedPageStaticProps as getStaticProps} from 'helpers/sharedPageStaticProps';
